import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  withStyles,
  makeStyles,
} from "@material-ui/core/styles";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  CircularProgress,
  Button,
  Grid,
  TextField,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from "@material-ui/core";

import { getList } from "../../../adminModel/exihibition";

import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import SearchIcon from "@material-ui/icons/Search";

// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },

  searchSection: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #eeeeee",
  },

  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  searchButton: {
    height: 40,
    minWidth: 100,
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  tableContainer: {
    maxHeight: 650,
  },

  table: {
    minWidth: 1050,
  },

  tableRow: {
    "&:hover": {
      backgroundColor: "#fafafa",
    },
  },

  cellText: {
    maxWidth: 220,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 13,
    color: "#444",
  },

  exhibitionName: {
    maxWidth: 220,
    fontSize: 13,
    fontWeight: 600,
    color: "#222",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },

  description: {
    maxWidth: 250,
    fontSize: 13,
    color: "#555",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },

  banner: {
    width: 110,
    height: 58,
    border: "1px solid #e5e5e5",
    borderRadius: 5,
    backgroundColor: "#fff",
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },

  statusBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#f3f3f3",
    color: "#555",
  },

  actionButton: {
    minWidth: 38,
    width: 38,
    height: 34,
    borderRadius: 5,
    marginRight: 6,
    padding: 0,
    boxShadow: "none",

    "&:last-child": {
      marginRight: 0,
    },

    "&:hover": {
      boxShadow: "none",
    },
  },

  pagination: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#fafafa",
    borderTop: "1px solid #eeeeee",
  },

  paginationButton: {
    borderRadius: 6,
  },

  pageNumber: {
    minWidth: 70,
    textAlign: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#555",
  },

  emptyCell: {
    padding: "50px !important",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// =====================================================
// TABLE CELL
// =====================================================

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: "#f7f7f7",
    color: "#222",
    fontWeight: 600,
    fontSize: 12,
    padding: "13px 14px",
    borderBottom: "1px solid #d8d8d8",
    textTransform: "uppercase",
    letterSpacing: "0.2px",
  },

  body: {
    fontSize: 13,
    padding: "11px 14px",
    color: "#444",
    borderBottom: "1px solid #e8e8e8",
    verticalAlign: "middle",
  },
}))(TableCell);

// =====================================================
// COMPONENT
// =====================================================

export default function CustomizedTables() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [start, setStart] = useState(0);
  const [disable, setDisable] = useState();
  const [getData, setGetdata] = useState([]);

  // ===================================================
  // FETCH DATA
  // ===================================================

  const FetchAuction = useCallback(
    async (
      currentSearch,
      currentStart,
      currentLimit
    ) => {
      setLoading(true);

      try {
        const results = await getList(
          currentSearch,
          currentStart,
          currentLimit
        );

        if (results === false) {
          setDisable(false);
          setGetdata([]);
        } else {
          setDisable(true);
          setGetdata(
            Array.isArray(results)
              ? results
              : []
          );
        }
      } catch (error) {
        console.error(
          "Fetch Error:",
          error
        );

        setDisable(false);
        setGetdata([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ===================================================
  // INITIAL FETCH
  // ===================================================

  useEffect(() => {
    FetchAuction(
      "",
      0,
      10
    );
  }, [FetchAuction]);

  // ===================================================
  // BACK
  // ===================================================

  const handleBackpage = () => {
    const newStart =
      Math.max(0, start - 10);

    setStart(newStart);
    setPage(
      Math.floor(newStart / 10)
    );

    FetchAuction(
      search,
      newStart,
      10
    );
  };

  // ===================================================
  // NEXT
  // ===================================================

  const handleNextpage = () => {
    const newStart =
      start + 10;

    setStart(newStart);
    setPage(
      Math.floor(newStart / 10)
    );

    FetchAuction(
      search,
      newStart,
      10
    );
  };

  // ===================================================
  // SEARCH
  // ===================================================

  const handleClickSearch = () => {
    setStart(0);
    setPage(0);

    FetchAuction(
      search,
      0,
      10
    );
  };

  // ===================================================
  // ENTER KEY
  // ===================================================

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleClickSearch();
    }
  };

  // ===================================================
  // FORMAT DATE
  // ===================================================

  const formatDate = (date) => {
    if (!date) {
      return "---";
    }

    return String(date).slice(
      0,
      10
    );
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Paper
      className={classes.root}
      elevation={0}
    >
      {/* ==========================
          SEARCH
      ========================== */}

      <Box
        className={
          classes.searchSection
        }
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
        >
          <Grid
            item
            md={4}
            sm={6}
            xs={12}
          >
            <TextField
              fullWidth
              size="small"
              type="text"
              label="Search Exhibition"
              name="search"
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              onKeyDown={
                handleKeyDown
              }
              placeholder="Search exhibition name"
              variant="outlined"
              className={
                classes.searchField
              }
            />
          </Grid>

          <Grid
            item
            md={2}
            sm={3}
            xs={12}
          >
            <Button
              fullWidth
              color="primary"
              variant="contained"
              startIcon={
                <SearchIcon />
              }
              onClick={
                handleClickSearch
              }
              className={
                classes.searchButton
              }
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ==========================
          TABLE
      ========================== */}

      <TableContainer
        className={
          classes.tableContainer
        }
      >
        <Table
          stickyHeader
          className={classes.table}
          aria-label="exhibition list"
          size="small"
        >
          <TableHead>
            <TableRow>

              <StyledTableCell>
                Exhibition Name
              </StyledTableCell>

              <StyledTableCell>
                Description
              </StyledTableCell>

              <StyledTableCell
                align="center"
              >
                Banner
              </StyledTableCell>

              <StyledTableCell>
                Artist
              </StyledTableCell>

              <StyledTableCell>
                Date Start
              </StyledTableCell>

              <StyledTableCell>
                Date End
              </StyledTableCell>

              <StyledTableCell
                align="center"
              >
                Status
              </StyledTableCell>

              <StyledTableCell
                align="center"
              >
                Action
              </StyledTableCell>

            </TableRow>
          </TableHead>

          <TableBody>
            {getData.length ? (
              getData
                .slice(
                  page * rowsPerPage,
                  page *
                    rowsPerPage +
                    rowsPerPage
                )
                .map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    className={
                      classes.tableRow
                    }
                  >

                    {/* EXHIBITION NAME */}

                    <StyledTableCell>
                      <Typography
                        className={
                          classes.exhibitionName
                        }
                      >
                        {row.exhibit_name ||
                          "---"}
                      </Typography>
                    </StyledTableCell>

                    {/* DESCRIPTION */}

                    <StyledTableCell>
                      <Tooltip
                        title={
                          row.exhibit_desc ||
                          ""
                        }
                        arrow
                      >
                        <Typography
                          className={
                            classes.description
                          }
                        >
                          {row.exhibit_desc ||
                            "---"}
                        </Typography>
                      </Tooltip>
                    </StyledTableCell>

                    {/* BANNER */}

                    <StyledTableCell
                      align="center"
                    >
                      <Box
                        className={
                          classes.banner
                        }
                        style={{
                          backgroundImage: `url(${
                            row.banner_url ===
                              null ||
                            !row.banner_url
                              ? "https://placehold.co/110x58?text=NO+IMAGE"
                              : row.banner_url
                          })`,
                        }}
                      />
                    </StyledTableCell>

                    {/* ARTIST */}

                    <StyledTableCell>
                      <Typography
                        className={
                          classes.cellText
                        }
                      >
                        {row.auctionType ||
                          "---"}
                      </Typography>
                    </StyledTableCell>

                    {/* DATE START */}

                    <StyledTableCell>
                      {formatDate(
                        row.date_start
                      )}
                    </StyledTableCell>

                    {/* DATE END */}

                    <StyledTableCell>
                      {formatDate(
                        row.date_end
                      )}
                    </StyledTableCell>

                    {/* SHOW STATUS */}

                    <StyledTableCell
                      align="center"
                    >
                      <span
                        className={
                          classes.statusBadge
                        }
                      >
                        {row.isVisible ===
                        1
                          ? "Show"
                          : "Unshow"}
                      </span>
                    </StyledTableCell>

                    {/* ACTION */}

                    <StyledTableCell
                      align="center"
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip
                          title="Edit Exhibition"
                          arrow
                        >
                          <Button
                            className={
                              classes.actionButton
                            }
                            color="primary"
                            variant="contained"
                            href={`/app/exhibtion/edit/${encodeURIComponent(
                              row.exhibit_name ||
                                ""
                            )}/${row.id}`}
                          >
                            <EditRoundedIcon
                              fontSize="small"
                            />
                          </Button>
                        </Tooltip>

                        <Tooltip
                          title="View Exhibition"
                          arrow
                        >
                          <Button
                            className={
                              classes.actionButton
                            }
                            color="primary"
                            variant="outlined"
                            href={`/app/exhibtion/view/item/${encodeURIComponent(
                              row.exhibit_name ||
                                ""
                            )}/${row.id}`}
                          >
                            <VisibilityIcon
                              fontSize="small"
                            />
                          </Button>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>

                  </TableRow>
                ))
            ) : (
              <TableRow>
                <StyledTableCell
                  colSpan={8}
                  align="center"
                  className={
                    classes.emptyCell
                  }
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    {loading
                      ? "Loading data..."
                      : "No exhibition records found."}
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ==========================
          PAGINATION
      ========================== */}

      <Box
        className={
          classes.pagination
        }
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <IconButton
          disabled={
            start === 0 ||
            loading
          }
          onClick={
            handleBackpage
          }
          size="small"
          className={
            classes.paginationButton
          }
        >
          <ArrowBackIosIcon fontSize="inherit" />
        </IconButton>

        <Typography
          className={
            classes.pageNumber
          }
        >
          Page {Math.floor(
            start / 10
          ) + 1}
        </Typography>

        <IconButton
          disabled={
            disable === false ||
            loading
          }
          onClick={
            handleNextpage
          }
          size="small"
          className={
            classes.paginationButton
          }
        >
          <ArrowForwardIosIcon fontSize="inherit" />
        </IconButton>
      </Box>

      {/* ==========================
          LOADING
      ========================== */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>
    </Paper>
  );
}